import type { z } from 'zod/v4';
import {
  ATTACHED_RESULT_SCHEMA_VERSION,
  SENTINEL_MAX_PAYLOAD_LENGTH,
} from './attached-result-constants.js';
import {
  attachedResultEnvelopeSchema,
  attachedResultPayloadSchemaByKind,
  attachedResultSourceSchema,
} from './contracts.js';
import { AttachedResultKind } from './enums.js';

// ════════════════════════════════════════════════════════════════════════════
// The envelope's TYPES, its totality guards, and the one way to read a stored
// block back. The schemas themselves live in `contracts.ts`; everything here
// is inferred from them, so there is no second spelling of the contract that
// could drift from the first.
// ════════════════════════════════════════════════════════════════════════════

export type AttachedResultPayloadByKind = {
  [K in AttachedResultKind]: z.infer<(typeof attachedResultPayloadSchemaByKind)[K]>;
};

/** §7.4's envelope type, verbatim: a mapped object indexed by `K`, so
 *  `AttachedResult<AttachedResultKind.Hotels>` narrows both `kind` and
 *  `payload` together and the bare `AttachedResult` is their union. */
export type AttachedResult<K extends AttachedResultKind = AttachedResultKind> = {
  [Kind in AttachedResultKind]: {
    schema: typeof ATTACHED_RESULT_SCHEMA_VERSION;
    kind: Kind;
    v: number;
    capturedAt: string;
    source: z.infer<typeof attachedResultSourceSchema>;
    title: string | null;
    payload: AttachedResultPayloadByKind[Kind];
  };
}[K];

export type AttachedResultEnvelope = z.infer<typeof attachedResultEnvelopeSchema>;

// ─── Compile-time totality guards ───────────────────────────────────────────
//
// The registries are only hermetic if something FAILS when they are not. These
// three aliases are that something: each resolves to `true` while the contract
// is total and to a type error the moment it is not. They cost nothing at
// runtime and are re-asserted as values in the suite, so a broken one is a red
// test as well as a red build.

type AssertTrue<T extends true> = T;
type IsExactly<A, B> = [A] extends [B] ? ([B] extends [A] ? true : false) : false;

/** Every kind reachable through the envelope union — no kind can be declared
 *  and then left unpersistable. */
type EnvelopeUnionKind = AttachedResultEnvelope['kind'];
export type AssertEveryKindIsInTheEnvelopeUnion = AssertTrue<
  IsExactly<AttachedResultKind, EnvelopeUnionKind>
>;

/** The zod-inferred envelope and the authored `AttachedResult` type describe
 *  the same object. Two spellings of one contract that drifted apart would be
 *  exactly the failure "zod is the single authority" exists to prevent. */
export type AssertEnvelopeMatchesAttachedResult = AssertTrue<
  IsExactly<AttachedResultEnvelope, AttachedResult>
>;

/** Every kind has a payload schema. Redundant with `contracts.ts`'s
 *  `satisfies` by design — the `satisfies` catches the missing entry, this
 *  catches an entry keyed by something that is not a kind. */
export type AssertEveryKindHasAPayloadSchema = AssertTrue<
  IsExactly<AttachedResultKind, keyof typeof attachedResultPayloadSchemaByKind>
>;

// ─── Reading one back ───────────────────────────────────────────────────────

/**
 * Parse a sentinel's payload text into an envelope, or null.
 *
 * Null covers every way stored content can fail to be one of ours — not JSON,
 * JSON of another shape, an envelope version this build does not know, a kind
 * that arrived from a newer deploy. NONE of those is an error condition here:
 * FE and BL deploy independently (Vercel and Railway), so meeting an
 * unreadable block is a normal Tuesday, and the ruled behaviour is a neutral
 * fallback render — never a crash, never a blank.
 */
export function parseAttachedResultEnvelope(input: {
  payloadText: string;
}): AttachedResultEnvelope | null {
  // Defensive ceiling BEFORE the parse, not after: stored content is edited
  // by hands this module does not control, and an unbounded `JSON.parse` over
  // a body someone pasted is the one thing this module must never do. A block
  // over the ceiling still DETECTS (so the patch guard still protects it) —
  // it simply does not render.
  if (input.payloadText.length > SENTINEL_MAX_PAYLOAD_LENGTH) return null;
  const parsed = safeJsonParse({ text: input.payloadText });
  if (parsed === null) return null;
  const result = attachedResultEnvelopeSchema.safeParse(parsed);
  return result.success ? result.data : null;
}

function safeJsonParse(input: { text: string }): unknown {
  try {
    return JSON.parse(input.text) as unknown;
  } catch {
    return null;
  }
}
